'use client';

import React, { useEffect, useState, FC, ChangeEvent, FormEvent } from 'react';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Loader2, ReplyIcon } from 'lucide-react';
import ChatQuoteFillIcon from 'remixicon-react/ChatQuoteFillIcon';
import { Input } from '@/components/ui/input';
import clsx from 'clsx';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
    validateAuthor,
    validateAuthorEmail,
    validateContent,
} from '@/utils/post/comments/ReviewValidation';
import { hasErrors } from '@/utils/post/comments/hasError';
import { createComment } from '@/utils/post/comments/createComment';
import addCommentMetas from '@/utils/post/comments/addCommentMeta';
import { usePrefillUserData } from '@/hooks/usePrefillUserData';
import useVerifyUser from '@/hooks/profile/useVerifyUser';
import LoginPrompt, {
    LOGIN_DIALOG_CLASS,
} from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/LoginPrompt';

import { updateUser } from '@/utils/profile/updateUser';
import { mutate } from 'swr';

/** Comment HTML → plain text for the quote (tags removed, common entities decoded). */
const commentText = (html: string) =>
    html
        .replace(/<[^>]*>?/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&quot;/g, '"')
        .replace(/&#0?39;|&#8217;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();

interface CommentAuthorNode {
    name: string;
}

interface CommentAuthor {
    node: CommentAuthorNode;
    roles: string[];
}

interface Comment {
    databaseId: number;
    authorRoles?: string[];
    author?: CommentAuthor;
    date?: string;
    rate?: number;
    content?: string;
    commentedOn?: {
        node: {
            databaseId: number;
        };
    };
}

interface FormErrors {
    [key: string]: string;
    author: string;
    content: string;
    authorEmail: string;
}

interface FormData {
    author: string;
    content: string;
    authorEmail: string;
    parent: number;
    errors: FormErrors;
}

interface ReplyProps {
    comment: Comment;
}

const Reply: FC<ReplyProps> = ({ comment }) => {
    const [open, setOpen] = useState<boolean>(false);
    const { prefilledName, prefilledEmail } = usePrefillUserData();
    const { isLoggedInGlobal } = useVerifyUser();
    const { toast } = useToast();

    const [formData, setFormData] = useState<FormData>({
        author: '',
        content: '',
        authorEmail: '',
        parent: comment.databaseId,
        errors: {
            author: '',
            content: '',
            authorEmail: '',
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [authorPhone, setAuthorPhone] = useState<string>('');

    const nameMissing = isLoggedInGlobal && !prefilledName;

    // Prepopulate if logged in
    useEffect(() => {
        if (isLoggedInGlobal) {
            setFormData((prev) => ({
                ...prev,
                author: prefilledName || '',
                authorEmail: prefilledEmail || 'no.mail@noemail.noemail',
            }));
        }
    }, [isLoggedInGlobal, prefilledName, prefilledEmail]);

    useEffect(() => {
        setAuthorPhone('');
        const phoneRegex = /^(00989|\+989|09)(\d{9})$/;
        if (phoneRegex.test(formData.authorEmail)) {
            setAuthorPhone(formData.authorEmail);
            setFormData((prev) => ({ ...prev, authorEmail: 'no.mail@noemail.noemail' }));
        }
    }, [formData.authorEmail]);

    const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);

        const skipAuthorValidation = isLoggedInGlobal && prefilledName;

        // Validate the form data
        const errors: FormErrors = {
            author: skipAuthorValidation ? '' : validateAuthor(formData.author),
            content: validateContent(formData.content),
            authorEmail: isLoggedInGlobal ? '' : validateAuthorEmail(formData.authorEmail),
        };

        if (hasErrors(errors)) {
            setFormData((prev) => ({ ...prev, errors }));
            setIsSubmitting(false);
        } else {
            try {
                // If user is logged in but was missing name, update profile first
                if (isLoggedInGlobal && nameMissing) {
                    const updateRes = await updateUser({
                        fullName: formData.author,
                        email: prefilledEmail || 'no.mail@noemail.noemail',
                    });

                    if (!updateRes || !updateRes.success) {
                        setIsSubmitting(false);
                        return; // Stop submission on update failure
                    }
                    await mutate('verifyUser');
                }

                const submitData = {
                    author: isLoggedInGlobal ? prefilledName || formData.author : formData.author,
                    authorEmail: isLoggedInGlobal
                        ? prefilledEmail || 'no.mail@noemail.noemail'
                        : formData.authorEmail,
                    content: formData.content,
                    parent: formData.parent,
                };

                const resCommentId: number = await createComment(
                    submitData,
                    comment?.commentedOn?.node?.databaseId || 0,
                );

                if (authorPhone) {
                    await addCommentMetas(resCommentId, 'commenter_phone', authorPhone);
                }

                toast({
                    className: 'bg-green-800 text-white rounded-2xl',
                    title: 'پاسخ شما با موفقیت ثبت شد',
                    description: 'پس از تایید مدیر نمایش داده می‌شود',
                });

                setOpen(false);

                // Reset form
                setFormData({
                    author: isLoggedInGlobal ? prefilledName || formData.author : '',
                    content: '',
                    authorEmail: isLoggedInGlobal
                        ? prefilledEmail && prefilledEmail !== 'no.mail@noemail.noemail'
                            ? prefilledEmail
                            : formData.authorEmail
                        : '',
                    parent: comment.databaseId,
                    errors: {
                        author: '',
                        content: '',
                        authorEmail: '',
                    },
                });
                setAuthorPhone('');
            } catch (error: any) {
                console.error(error);

                const errorMessage = error.message || 'در فرایند ثبت پاسخ شما مشکلی پیش آمد!';
                toast({
                    className: 'bg-red-800 text-white rounded-2xl',
                    title: 'خطا در ثبت',
                    description: errorMessage,
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
            errors: {
                ...prev.errors,
                [name]: '',
            },
        }));
    };

    const isAdminAuthor = (comment?.authorRoles || []).some((r) =>
        ['administrator', 'editor'].includes(r),
    );
    // Same name the list shows for staff replies.
    const authorName = isAdminAuthor ? 'خانه سرمایه' : comment?.author?.node?.name || 'دیدگاه';
    // Plain text, never HTML: the quote used to be injected with dangerouslySetInnerHTML.
    const quote = commentText(comment?.content || '');
    const contentLength = formData.content.trim().length;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger
                suppressHydrationWarning
                className={
                    'flex items-center border border-gray-200 gap-2 dark:border-white/10 bg-gray-100 hover:bg-gray-200 text-black px-2 py-1 dark:bg-white/15 dark:hover:bg-white/20 transition-all dark:text-white/80 cursor-pointer rounded-md text-xs'
                }
            >
                پاسخ
                <ReplyIcon className={'-scale-100'} size={20} />
            </DialogTrigger>
            <DialogContent
                dir='rtl'
                className={
                    isLoggedInGlobal
                        ? 'w-[calc(100%-2rem)] max-w-lg gap-4 rounded-2xl border-black/5 bg-white p-5 text-right dark:border-white/10 dark:bg-primary-active'
                        : LOGIN_DIALOG_CLASS
                }
            >
                {!isLoggedInGlobal ? (
                    <LoginPrompt
                        reason='برای پاسخ دادن وارد شوید'
                        hint='بعد از ورود، فرم پاسخ همین‌جا باز می‌شود.'
                        onSuccess={async () => {
                            // Refresh the login state at once (it is cached for 5 minutes),
                            // so the reply form shows up instead of the login form.
                            await mutate('verifyUser');
                            toast({
                                className: 'bg-green-800 text-white rounded-2xl',
                                title: 'ورود با موفقیت انجام شد',
                            });
                        }}
                    />
                ) : (
                    <>
                        <DialogHeader className='space-y-0'>
                            <DialogTitle className='flex items-center gap-2 text-base text-gray-900 dark:text-white'>
                                <span className='flex size-8 items-center justify-center rounded-full bg-progress/10 text-progress dark:bg-white/10 dark:text-white'>
                                    <ChatQuoteFillIcon size={16} />
                                </span>
                                پاسخ به {authorName}
                            </DialogTitle>
                            <DialogDescription className='sr-only'>
                                متن پاسخ خود را بنویسید و ثبت کنید.
                            </DialogDescription>
                        </DialogHeader>

                        {quote && (
                            <blockquote className='rounded-xl border-r-4 border-progress/60 bg-gray-50 px-3 py-2 text-xs leading-6 text-gray-600 dark:bg-white/5 dark:text-white/70'>
                                <p className='line-clamp-3'>{quote}</p>
                            </blockquote>
                        )}

                        <form onSubmit={handleFormSubmit} className='flex flex-col gap-3'>
                            {nameMissing && (
                                <div className='flex flex-col gap-1'>
                                    <Input
                                        placeholder='نام شما'
                                        type='text'
                                        aria-invalid={!!formData.errors.author}
                                        className={clsx(
                                            'bg-gray-50 text-base text-primary placeholder:text-xs dark:border-white/10 dark:bg-white/5 dark:text-white',
                                            formData.errors.author && 'border-red-500',
                                        )}
                                        id='author'
                                        name='author'
                                        value={formData.author}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formData.errors.author && (
                                        <p className='text-xs text-red-500'>
                                            {formData.errors.author}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className='flex flex-col gap-1'>
                                <Textarea
                                    autoFocus
                                    placeholder='پاسخ خود را بنویسید...'
                                    aria-invalid={!!formData.errors.content}
                                    className={clsx(
                                        'min-h-28 resize-y rounded-xl bg-gray-50 text-base leading-7 text-primary placeholder:text-sm dark:border-white/10 dark:bg-white/5 dark:text-white',
                                        formData.errors.content && 'border-red-500',
                                    )}
                                    id='content'
                                    name='content'
                                    value={formData.content}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) => {
                                        // Ctrl/⌘ + Enter sends
                                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                            e.currentTarget.form?.requestSubmit();
                                        }
                                    }}
                                    required
                                />
                                {formData.errors.content && (
                                    <p className='text-xs text-red-500'>
                                        {formData.errors.content}
                                    </p>
                                )}
                            </div>

                            <p className='text-xs text-gray-500 dark:text-white/50'>
                                پاسخ شما پس از تایید مدیر نمایش داده می‌شود.
                            </p>

                            <div className='flex items-center justify-end gap-2'>
                                <DialogClose
                                    type='button'
                                    className='rounded-lg px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/10'
                                >
                                    انصراف
                                </DialogClose>
                                <Button
                                    className='min-w-28 rounded-lg bg-progress font-normal text-white hover:bg-progress/90 disabled:opacity-50'
                                    type='submit'
                                    disabled={isSubmitting || contentLength < 2}
                                >
                                    {isSubmitting ? (
                                        <Loader2 className='size-5 animate-spin' />
                                    ) : (
                                        'ثبت پاسخ'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default Reply;
